import { ComponentChildren } from "preact";
import { Children } from "../types";
import {HTMLAttributes} from "react";
import {ComponentError} from "../error";
import styled from "styled-components";
import {Scrollbox} from "./scrollbox";
import {Accents, getAccentStyle, useAccent, withAccent} from "../../theme/accents";
import {Table} from "./table";

export type CardProps = {
    class?: string;
    title?: ComponentChildren;
    actions?: ComponentChildren[];
    header?: ComponentChildren;
    footer?: ComponentChildren;
    fullHeight?: boolean;
} & Children & HTMLAttributes<HTMLElement>;

const CardHeader = styled.header<Accents>`
    padding: 0.5rem;
    background: ${props => getAccentStyle(props).primaryBg};
    color: ${props => getAccentStyle(props).primaryFg};
`;

const Heading = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    font-size: 1.1rem;
`;

const Footer = styled.footer`
    padding: .5rem;
`;

const Title = styled.h1`
    font-size: 1.1rem;
    margin: 0;
`;

export const InnerCard = styled(function({"class": clz, className, title, actions, header, footer, children, ...props}: CardProps) {
    const accent = useAccent();

    return <div className={className}>
        {(title || actions || header) ? <CardHeader accent={accent}>{
            (title || actions) ? <Heading>
                <span></span>
                <Title>{title}</Title>
                <span>{actions}</span>
            </Heading> : null
        }{header}
        </CardHeader> : null}
        <main class={clz} {...props}>
            <ComponentError>
                {children}
            </ComponentError>
        </main>
        {footer && <Footer>{footer}</Footer>}
    </div>;
})`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow-x: auto;
    max-width: 100%;
    position: relative;

    > main, > footer {
        background: ${props => getAccentStyle(props).cardBg};
        color: ${props => getAccentStyle(props).cardFg};
        
        &:has(> ${Table}) {
            display: flex;
            flex-direction: column;
            background: transparent;
            
            .table-spacer {
                flex-grow: 1;
                background: ${props => getAccentStyle(props).cardBg};
            }
        }
    }
    
    > main {
        
        flex-grow: 1;
        overflow: auto;
        
        > form:only-child {
            padding: .5rem;
            display: flex;
            flex-direction: column;
        }
    }
`;

export const Card = styled(withAccent(function({className, ...props}: CardProps & Accents) {
    return <Scrollbox className={className}>
        <InnerCard {...props} />
    </Scrollbox>;
}, "default"))`
    + ${Scrollbox} {
        margin-top: 1rem;
    }
    
    height: ${props => props.fullHeight ?? true ? "100%" : "auto"};
`;
